import React, { useState } from 'react';
import { Users, MapPin, FileText, Plus } from 'lucide-react';

interface ElementsEnqueteTabProps {
  enquete: any;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onAddPerson?: () => void;
  onAddLocation?: () => void;
  onAddNote?: () => void;
}

const ElementsEnqueteTab: React.FC<ElementsEnqueteTabProps> = ({
  enquete,
  activeTab = 'personnes',
  onTabChange,
  onAddPerson,
  onAddLocation,
  onAddNote
}) => {
  const [currentTab, setCurrentTab] = useState(activeTab);

  const handleTabChange = (tab: string) => {
    setCurrentTab(tab);
    onTabChange?.(tab);
  };

  const subTabs = [
    { 
      id: 'personnes', 
      label: 'Personnes', 
      icon: Users, 
      count: enquete?.personnes?.length || 0,
      onAdd: onAddPerson 
    },
    { 
      id: 'lieux', 
      label: 'Lieux', 
      icon: MapPin, 
      count: enquete?.lieux?.length || 0,
      onAdd: onAddLocation 
    },
    { 
      id: 'notes', 
      label: 'Notes', 
      icon: FileText, 
      count: enquete?.notes?.length || 0,
      onAdd: onAddNote 
    }
  ];

  const renderPersonnesSection = () => (
    <div className="space-y-4">
      {enquete?.personnes?.length > 0 ? (
        enquete.personnes.map((personne: any, index: number) => (
          <div key={index} className="enhanced-card p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg mb-2">{personne.nom || 'Nom inconnu'}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {personne.prenom && (
                    <div>
                      <span className="text-gray-400">Prénom:</span>
                      <span className="text-white ml-2">{personne.prenom}</span>
                    </div>
                  )}
                  {personne.dateNaissance && (
                    <div>
                      <span className="text-gray-400">Date de naissance:</span>
                      <span className="text-white ml-2">{personne.dateNaissance}</span>
                    </div>
                  )}
                  {personne.profession && (
                    <div>
                      <span className="text-gray-400">Profession:</span>
                      <span className="text-white ml-2">{personne.profession}</span>
                    </div>
                  )}
                  {personne.telephone && (
                    <div>
                      <span className="text-gray-400">Téléphone:</span>
                      <span className="text-white ml-2">{personne.telephone}</span>
                    </div>
                  )}
                  {personne.email && (
                    <div>
                      <span className="text-gray-400">Email:</span>
                      <span className="text-white ml-2">{personne.email}</span>
                    </div>
                  )}
                  {personne.adresse && (
                    <div className="md:col-span-2">
                      <span className="text-gray-400">Adresse:</span>
                      <span className="text-white ml-2">{personne.adresse}</span>
                    </div>
                  )}
                </div>
                {personne.notes && (
                  <div className="mt-4 p-3 bg-osint-light-gray/50 rounded-lg">
                    <span className="text-gray-400 text-sm">Notes:</span>
                    <p className="text-white text-sm mt-1">{personne.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="empty-state">
          <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune personne ajoutée</h3>
          <p className="text-gray-400 mb-6">Commencez par ajouter une personne d'intérêt à cette enquête</p>
          <button 
            onClick={onAddPerson}
            className="empty-state-action"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une personne
          </button>
        </div>
      )}
    </div>
  );

  const renderLieuxSection = () => (
    <div className="space-y-4">
      {enquete?.lieux?.length > 0 ? (
        enquete.lieux.map((lieu: any, index: number) => (
          <div key={index} className="enhanced-card p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-lg mb-2">{lieu.nom || 'Lieu sans nom'}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {lieu.adresse && (
                    <div className="md:col-span-2">
                      <span className="text-gray-400">Adresse:</span>
                      <span className="text-white ml-2">{lieu.adresse}</span>
                    </div>
                  )}
                  {lieu.type && (
                    <div>
                      <span className="text-gray-400">Type:</span>
                      <span className="text-white ml-2">{lieu.type}</span>
                    </div>
                  )}
                  {lieu.coordonnees && (
                    <div>
                      <span className="text-gray-400">Coordonnées:</span>
                      <span className="text-white ml-2">{lieu.coordonnees}</span>
                    </div>
                  )}
                </div>
                {lieu.notes && (
                  <div className="mt-4 p-3 bg-osint-light-gray/50 rounded-lg">
                    <span className="text-gray-400 text-sm">Notes:</span>
                    <p className="text-white text-sm mt-1">{lieu.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="empty-state">
          <MapPin className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun lieu ajouté</h3>
          <p className="text-gray-400 mb-6">Ajoutez des lieux d'intérêt pour cette enquête</p>
          <button 
            onClick={onAddLocation}
            className="empty-state-action"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un lieu
          </button>
        </div>
      )}
    </div>
  );

  const renderNotesSection = () => (
    <div className="space-y-4">
      {enquete?.notes?.length > 0 ? (
        enquete.notes.map((note: any, index: number) => (
          <div key={index} className="enhanced-card p-6">
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-white font-semibold text-lg">{note.titre || `Note ${index + 1}`}</h4>
              <span className="text-gray-400 text-sm">{note.date}</span>
            </div>
            <p className="text-gray-300 mb-3">{note.contenu}</p>
            {note.auteur && (
              <div className="text-sm text-gray-400">
                Par: {note.auteur}
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="empty-state">
          <FileText className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune note ajoutée</h3>
          <p className="text-gray-400 mb-6">Ajoutez des notes pour documenter vos observations</p>
          <button 
            onClick={onAddNote}
            className="empty-state-action"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une note
          </button>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (currentTab) {
      case 'personnes':
        return renderPersonnesSection();
      case 'lieux':
        return renderLieuxSection();
      case 'notes':
        return renderNotesSection();
      default:
        return renderPersonnesSection();
    }
  };

  return (
    <div className="elements-tab">
      {/* Navigation à onglets */}
      <div className="tab-navigation">
        {subTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`tab-button ${currentTab === tab.id ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4 mr-2" />
              <span>{tab.label}</span>
              <span className="ml-2 px-2 py-1 text-xs bg-osint-orange/20 text-osint-orange rounded-full">
                {tab.count}
              </span>
              {currentTab === tab.id && tab.onAdd && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    tab.onAdd?.();
                  }}
                  className="ml-auto p-1 hover:bg-osint-orange/20 rounded"
                  title={`Ajouter ${tab.label.toLowerCase()}`}
                >
                  <Plus className="w-3 h-3" />
                </button>
              )}
            </button>
          );
        })}
      </div>

      {/* Contenu de l'onglet */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ElementsEnqueteTab;